import Dexie, { Table } from 'dexie';
import { isOfflineCapableNative } from '../utils/platform';
import { FileRef, deleteFile, deleteTableFiles, readBlob, writeBlob } from '../../offline/storage/offline-files';
import { offlineDb } from '../../offline/offline-db';
import type { ConsignmentDocType, ShareTarget } from './shared-files.service';

/**
 * Disk copy of the files waiting in SharedFilesService, so a page reload / app restart does not lose them
 * (SHARE_TO_LUMBERLINQ_PLAN.md).
 *
 *  - PHONE APP (Android/iOS): the small "what is waiting" record lives in the app's offline store (`offlineDb.meta`,
 *    the encrypted SQLite store once a phone has moved — OFFLINE_SQLITE_ONLY_PLAN.md), so it is wiped with the
 *    rest of the offline data on logout / user switch. The file BYTES are plain files in the app's private,
 *    never-backed-up folder (offline-files.ts, phone-storage encryption). No IndexedDB of its own is created.
 *  - WEBSITE: it has no other offline storage, so this file keeps its own tiny IndexedDB (`ll-shared-files`) with the
 *    bytes inside the record — kept at most 12 h, cleared on attach / discard / logout.
 * Every call is best-effort: if storage refuses (private mode, storage full) the service keeps the files in memory.
 */
interface StoredFile {
  name: string;
  type: string;
  lastModified: number;
  /** Website only. */
  blob?: Blob;
  /** Phone app only. */
  ref?: FileRef;
}

interface StoredPending {
  key: string;
  /** "<email>|<tenantId>" the files were collected for — restored only for the same user and company. */
  owner: string;
  savedAt: number;
  target: ShareTarget | null;
  docType?: ConsignmentDocType;
  files: StoredFile[];
}

export interface LoadedShared {
  owner: string;
  savedAt: number;
  target: ShareTarget | null;
  docType?: ConsignmentDocType;
  files: File[];
}

const KEY = 'current';
const META_KEY = 'sharedFilesPending';
const FILE_TABLE = 'shared-files';

// ── website store (IndexedDB) ────────────────────────────────────────────────────────────────────────────────
class SharedFilesDb extends Dexie {
  pending!: Table<StoredPending, string>;

  constructor() {
    super('ll-shared-files');
    this.version(1).stores({ pending: 'key' });
  }
}

let db: SharedFilesDb | null = null;

function openWebDb(): SharedFilesDb | null {
  try {
    if (typeof indexedDB === 'undefined') return null;
    db = db ?? new SharedFilesDb();
    return db;
  } catch {
    return null;
  }
}

// ── phone store (offline store + private files) ──────────────────────────────────────────────────────────────

/** File -> the on-disk reference already written for it, so a later save does not write the same bytes again. */
const writtenRefs = new WeakMap<Blob, FileRef>();

/** A failing READ throws (callers stop instead of treating it as "nothing waiting" and deleting files); only an
 *  unreadable record body counts as empty. */
async function readPhoneRow(): Promise<StoredPending | null> {
  const entry = await offlineDb.meta.get(META_KEY);
  if (!entry?.value) return null;
  try {
    return JSON.parse(entry.value) as StoredPending;
  } catch {
    return null;
  }
}

function refsOf(row: StoredPending | null | undefined): FileRef[] {
  return (row?.files ?? []).map(f => f.ref).filter((r): r is FileRef => !!r);
}

async function savePhone(
  owner: string, target: ShareTarget | null, docType: ConsignmentDocType | undefined, files: File[],
): Promise<void> {
  const previous = await readPhoneRow();
  const stored: StoredFile[] = [];
  for (const f of files) {
    let ref = writtenRefs.get(f);
    if (!ref) {
      ref = await writeBlob(FILE_TABLE, f);
      writtenRefs.set(f, ref);
    }
    stored.push({ name: f.name, type: f.type, lastModified: f.lastModified, ref });
  }
  const row: StoredPending = { key: KEY, owner, savedAt: Date.now(), target, docType, files: stored };
  await offlineDb.meta.put({ key: META_KEY, value: JSON.stringify(row) });
  // Delete on-disk copies of files that are no longer waiting (removed one by one, or replaced).
  const keep = new Set(stored.map(s => s.ref?.__llFile));
  for (const r of refsOf(previous)) {
    if (!keep.has(r.__llFile)) await deleteFile(r);
  }
}

async function loadPhone(): Promise<LoadedShared | null> {
  const row = await readPhoneRow();
  if (!row) {
    // Nothing waiting (or the record was wiped by a logout / user switch): remove any file left behind.
    await deleteTableFiles(FILE_TABLE);
    return null;
  }
  const files: File[] = [];
  for (const s of row.files ?? []) {
    if (!s.ref) continue;
    try {
      const b = await readBlob(s.ref);
      const file = b instanceof File ? b : new File([b], s.name, { type: s.type, lastModified: s.lastModified });
      writtenRefs.set(file, s.ref); // already on disk — do not write it again on the next save
      files.push(file);
    } catch {
      /* that file is gone from disk — skip it */
    }
  }
  return { owner: row.owner, savedAt: row.savedAt, target: row.target, docType: row.docType, files };
}

async function clearPhone(): Promise<void> {
  await offlineDb.meta.delete(META_KEY);
  await deleteTableFiles(FILE_TABLE);
}

// ── public API ───────────────────────────────────────────────────────────────────────────────────────────────

export async function saveSharedFiles(
  owner: string,
  target: ShareTarget | null,
  docType: ConsignmentDocType | undefined,
  files: File[],
): Promise<void> {
  try {
    if (isOfflineCapableNative()) {
      await savePhone(owner, target, docType, files);
      return;
    }
    const d = openWebDb();
    if (!d) return;
    await d.pending.put({
      key: KEY,
      owner,
      savedAt: Date.now(),
      target,
      docType,
      files: files.map(f => ({ name: f.name, type: f.type, lastModified: f.lastModified, blob: f })),
    });
  } catch {
    /* quota / private mode — memory-only fallback */
  }
}

export async function loadSharedFiles(): Promise<LoadedShared | null> {
  try {
    if (isOfflineCapableNative()) return await loadPhone();
    const d = openWebDb();
    if (!d) return null;
    const row = await d.pending.get(KEY);
    if (!row) return null;
    const files = (row.files ?? [])
      .filter(s => !!s.blob)
      .map(s => new File([s.blob as Blob], s.name, { type: s.type, lastModified: s.lastModified }));
    return { owner: row.owner, savedAt: row.savedAt, target: row.target, docType: row.docType, files };
  } catch {
    return null;
  }
}

export async function clearSharedFiles(): Promise<void> {
  try {
    if (isOfflineCapableNative()) {
      await clearPhone();
      return;
    }
    const d = openWebDb();
    if (d) await d.pending.delete(KEY);
  } catch {
    /* nothing to do */
  }
}
